/**
 * Hook for Ministers List Report data fetching and state management
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMinistersInRequests, getActiveMinisterCountByDegree, getActiveMinisters } from "../services/reports.service";
import { REPORT_QUERY_KEYS } from "../queryKeys";
import type { MinisterSystemDegreeCount } from "../services/reports.service";
import type { MinisterInRequest, MinistersListReportFilters, MinistersListReportSummary, ActiveMinister } from "../types";
import { CredentialType, CredentialRequestStatus } from "@/models/credentialRequest";

export type { MinisterSystemDegreeCount };

/**
 * Default filters for ministers list report
 * - Current year
 * - Active statuses (exclude DRAFT and REJECTED)
 * - MINISTERIAL type only
 */
export function getDefaultFilters(): MinistersListReportFilters {
  const currentYear = new Date().getFullYear();
  
  return {
    dateFrom: `${currentYear}-01-01`,
    dateTo: `${currentYear}-12-31`,
    credentialTypes: [CredentialType.MINISTERIAL],
    statuses: [
      CredentialRequestStatus.SUBMITTED,
      CredentialRequestStatus.APPROVED,
      CredentialRequestStatus.PRINTED,
      CredentialRequestStatus.DELIVERED,
    ],
    districtId: null,
    regionId: null,
    degreeId: null,
    search: "",
  };
}

/**
 * Hook for managing ministers list report state
 */
export function useMinistersListReport() {
  const [filters, setFilters] = useState<MinistersListReportFilters>(getDefaultFilters());

  // Fetch filtered report data
  const {
    data: ministers = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["ministersListReport", filters],
    queryFn: () => getMinistersInRequests(filters),
    staleTime: 15 * 60 * 1000, // 15 min — heavy 9-JOIN view; filter-keyed so each combo cached
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch system-wide degree counts for comparison (independent of filters)
  const { data: systemDegreeCounts = [], isLoading: isLoadingSystemCounts } = useQuery({
    queryKey: REPORT_QUERY_KEYS.ministerSystemDegreeCounts,
    queryFn: getActiveMinisterCountByDegree,
    staleTime: Infinity,        // system aggregate — only changes via minister writes
    gcTime: 8 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch ALL active ministers (used to compute uncredentialed tab)
  const { data: allActiveMinisters = [], isLoading: isLoadingAllMinisters } = useQuery({
    queryKey: REPORT_QUERY_KEYS.allActiveMinisters,
    queryFn: getActiveMinisters,
    staleTime: Infinity,        // same roster data as ministerRegistry — invalidated by writes
    gcTime: 4 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Calculate summary statistics
  const summary = useMemo<MinistersListReportSummary>(() => {
    return calculateSummary(ministers);
  }, [ministers]);

  // Inactive ministers who appear in the current filtered requests.
  // Deduplicated by minister_id — one row per person (latest request row kept).
  const inactivatedMinisters = useMemo<MinisterInRequest[]>(() => {
    const seen = new Set<string>();
    return ministers.filter((m) => {
      if (m.minister_is_active) return false;
      if (seen.has(m.minister_id)) return false;
      seen.add(m.minister_id);
      return true;
    });
  }, [ministers]);

  // Ministers NOT in any request matching current filters.
  // Diff is computed client-side: all active ministers minus those already in
  // the report. Territory / degree filters are applied to the minister's own
  // district/region (not the request territory).
  const uncredentialedMinisters = useMemo<ActiveMinister[]>(() => {
    if (!allActiveMinisters.length) return [];

    const inRequestIds = new Set(ministers.map((m) => m.minister_id));

    return allActiveMinisters.filter((m) => {
      if (inRequestIds.has(m.id)) return false;
      if (filters.districtId && m.district_id !== filters.districtId) return false;
      if (filters.regionId && m.region_id !== filters.regionId) return false;
      if (filters.degreeId && m.degree_id !== filters.degreeId) return false;
      if (filters.search?.trim()) {
        // Token logic: split on whitespace, require ALL tokens to match (AND),
        // normalise ADB codes to allow dash-free matching.
        const tokens = filters.search
          .trim()
          .toLowerCase()
          .split(/\s+/)
          .filter((t) => t.length > 0);
        const normalizedAdb = (m.adb_code || "").replace(/-/g, "");
        const searchableText =
          `${m.full_name || ""} ${m.dni || ""} ${m.adb_code || ""} ${normalizedAdb}`.toLowerCase();
        if (!tokens.every((token) => searchableText.includes(token))) return false;
      }
      return true;
    });
  }, [allActiveMinisters, ministers, filters.districtId, filters.regionId, filters.degreeId, filters.search]);

  // Update individual filter
  const updateFilter = <K extends keyof MinistersListReportFilters>(
    key: K,
    value: MinistersListReportFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Reset to defaults
  const resetFilters = () => {
    setFilters(getDefaultFilters());
  };

  // Toggle a credential type
  const toggleCredentialType = (type: CredentialType) => {
    setFilters((prev) => {
      const types = prev.credentialTypes.includes(type)
        ? prev.credentialTypes.filter((t) => t !== type)
        : [...prev.credentialTypes, type];
      return { ...prev, credentialTypes: types };
    });
  };

  // Toggle a status
  const toggleStatus = (status: CredentialRequestStatus) => {
    setFilters((prev) => {
      const statuses = prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status];
      return { ...prev, statuses: statuses };
    });
  };

  return {
    ministers,
    filters,
    summary,
    systemDegreeCounts,
    uncredentialedMinisters,
    inactivatedMinisters,
    isLoading,
    isLoadingSystemCounts,
    isLoadingAllMinisters,
    error,
    updateFilter,
    resetFilters,
    toggleCredentialType,
    toggleStatus,
    refetch,
  };
}

/**
 * Calculate summary statistics from ministers data
 */
function calculateSummary(ministers: MinisterInRequest[]): MinistersListReportSummary {
  const totalMinisters = ministers.length;

  // Count unique requests and unique ministers
  const uniqueRequestIds = new Set(ministers.map(m => m.credential_request_id).filter(Boolean));
  const uniqueMinisterIds = new Set(ministers.map(m => m.minister_id).filter(Boolean));
  const activeUniqueMinisterIds = new Set(
    ministers.filter(m => m.minister_is_active).map(m => m.minister_id).filter(Boolean)
  );
  const uniqueRequests = uniqueRequestIds.size;
  const uniqueMinisters = uniqueMinisterIds.size;
  const activeUniqueMinisters = activeUniqueMinisterIds.size;

  // Count by degree
  const degreeMap = new Map<string, { degreeId: string; degreeName: string; count: number }>();
  ministers.forEach((m) => {
    if (m.degree_id && m.degree_name) {
      const existing = degreeMap.get(m.degree_id);
      if (existing) {
        existing.count++;
      } else {
        degreeMap.set(m.degree_id, {
          degreeId: m.degree_id,
          degreeName: m.degree_name,
          count: 1,
        });
      }
    }
  });
  const byDegree = Array.from(degreeMap.values()).sort((a, b) => b.count - a.count);

  // Count by credential type
  const typeMap = new Map<string, number>();
  ministers.forEach((m) => {
    if (m.credential_type) {
      typeMap.set(m.credential_type, (typeMap.get(m.credential_type) || 0) + 1);
    }
  });
  const byCredentialType = Array.from(typeMap.entries()).map(([type, count]) => ({
    credentialType: type as CredentialType,
    count,
  }));

  // Count by status
  const statusMap = new Map<string, number>();
  ministers.forEach((m) => {
    if (m.request_status) {
      statusMap.set(m.request_status, (statusMap.get(m.request_status) || 0) + 1);
    }
  });
  const byStatus = Array.from(statusMap.entries()).map(([status, count]) => ({
    status: status as CredentialRequestStatus,
    count,
  }));

  return {
    totalMinisters,
    uniqueRequests,
    uniqueMinisters,
    activeUniqueMinisters,
    byDegree,
    byCredentialType,
    byStatus,
  };
}
